import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Hourglass, Trash2, UserPlus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import AddOneOffMemberPicker from "../attendance/AddOneOffMemberPicker";
import { Session } from "../../features/sessions/models/Session";
import { buildAttendanceRoster } from "../../features/sessions/utils/buildAttendanceRoster";
import { selectSessionWaitlist } from "../../features/sessions/waitlist-slice";
import {
  addWaitlistEntry,
  convertWaitlistEntry,
  fetchSessionWaitlist,
  removeWaitlistEntry,
} from "../../features/sessions/slice-extensions/waitlist-slice-extension";
import { fetchSessionById } from "../../features/sessions/slice-extensions/sessions-slice-extension";
import { fetchMembers } from "../../features/members/slice-extensions/members-slice-extension";
import { selectGroups, selectMembers } from "../../features/members/members-slice";
import { BookingStatus } from "../../features/sessions/models/BookingStatus";
import { OccurrenceOccupancy } from "../../features/sessions/models/OccurrenceOccupancy";
import { fetchSessionOccupancy } from "../../features/sessions/slice-extensions/occupancy-slice-extension";
import { fullestOccupancy, isoDayFromToday } from "../../features/sessions/utils/fullestOccupancy";
import { formatSeatShortDate } from "../../features/sessions/utils/seatDateTime";

interface Props {
  session: Session;
}

/** How far ahead the spots badge looks for the fullest date (eight weeks). */
const OCCUPANCY_DAYS = 56;

/**
 * The session's waitlist: spots filled vs capacity, queued members oldest
 * first, and one-click convert onto the roster when a spot is free.
 */
const SessionWaitlistCard = ({ session }: Props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const entries = useAppSelector(selectSessionWaitlist(session.id));
  const members = useAppSelector(selectMembers);
  const groups = useAppSelector(selectGroups);
  const [busyEntryId, setBusyEntryId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchSessionWaitlist(session.id));
    if (members.length === 0) {
      dispatch(fetchMembers());
    }
  }, [dispatch, session.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const roster = useMemo(
    () => buildAttendanceRoster(session, members, groups),
    [session, members, groups],
  );

  // The fullest upcoming date in the next eight weeks: roster plus that date's seats.
  const [fullest, setFullest] = useState<OccurrenceOccupancy | null>(null);
  useEffect(() => {
    if (session.capacity == null) {
      setFullest(null);
      return;
    }
    let active = true;
    dispatch(
      fetchSessionOccupancy({ bookingId: session.id, from: isoDayFromToday(0), to: isoDayFromToday(OCCUPANCY_DAYS) }),
    ).then((result) => {
      if (active && fetchSessionOccupancy.fulfilled.match(result)) setFullest(fullestOccupancy(result.payload));
    });
    return () => {
      active = false;
    };
  }, [dispatch, session.id, session.capacity, roster.totalCount]);

  const isFull = fullest
    ? fullest.isFull
    : session.capacity != null && roster.totalCount >= session.capacity;
  const memberName = (memberId: string) => {
    const member = members.find((m) => m.id === memberId);
    return member
      ? `${member.firstName} ${member.lastName}`.trim()
      : t("waitlist.unknownMember", "Member");
  };

  const handleAdd = async (memberId: string) => {
    try {
      await dispatch(addWaitlistEntry({ sessionId: session.id, request: { memberId, note: null } }))
        .unwrap();
    } catch (error) {
      toast.error((error as { message?: string })?.message || t("waitlist.addFailed", "Adding failed"));
    }
  };

  const handleConvert = async (entryId: string) => {
    setBusyEntryId(entryId);
    try {
      await dispatch(convertWaitlistEntry({ sessionId: session.id, entryId })).unwrap();
      await dispatch(fetchSessionById(session.id));
      toast.success(t("waitlist.movedToast", "Moved to the roster"));
    } catch (error) {
      toast.error(
        (error as { message?: string })?.message || t("waitlist.moveFailed", "Moving failed"),
      );
    } finally {
      setBusyEntryId(null);
    }
  };

  if (session.status === BookingStatus.Cancelled && entries.length === 0) {
    return null;
  }

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-grey-20 dark:text-white">
          <Hourglass size={20} />
          {t("waitlist.title", "Waitlist")}
        </h2>
        {session.capacity != null && (
          <span
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              isFull
                ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
            }`}
          >
            {fullest
              ? t("waitlist.spotsFilledOn", {
                  filled: fullest.occupied,
                  capacity: session.capacity,
                  date: formatSeatShortDate(fullest.date, i18n.language),
                })
              : t("waitlist.spotsFilled", "{{filled}} / {{capacity}} spots filled", {
                  filled: roster.totalCount,
                  capacity: session.capacity,
                })}
          </span>
        )}
      </div>

      {entries.length === 0 ? (
        <p className="mt-3 text-sm text-grey-40 dark:text-grey-80">
          {t("waitlist.empty", "No one is waiting.")}
        </p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {entries.map((entry, index) => (
            <li
              key={entry.id}
              className="flex items-center justify-between gap-3 rounded-xl bg-grey-95 px-3 py-2 dark:bg-primary-20"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-grey-20 dark:text-white">
                  {index + 1}. {memberName(entry.memberId)}
                </p>
                <p className="truncate text-xs text-grey-40 dark:text-grey-80">
                  {new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(
                    new Date(entry.dateCreated),
                  )}
                  {entry.note ? ` · ${entry.note}` : ""}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-40 hover:bg-white dark:text-primary-90 dark:hover:bg-primary-10"
                  disabled={busyEntryId !== null}
                  title={
                    isFull
                      ? t("waitlist.fullHint", "Session is full — free a spot or raise the capacity")
                      : undefined
                  }
                  onClick={() => handleConvert(entry.id)}
                >
                  <UserPlus size={14} />
                  {t("waitlist.moveToRoster", "Move to roster")}
                </button>
                <button
                  type="button"
                  aria-label={t("waitlist.remove", "Remove from waitlist")}
                  title={t("waitlist.remove", "Remove from waitlist")}
                  className="rounded-md p-1 text-grey-40 hover:text-red-700 dark:text-grey-80 dark:hover:text-red-400"
                  disabled={busyEntryId !== null}
                  onClick={() => dispatch(removeWaitlistEntry({ sessionId: session.id, entryId: entry.id }))}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {session.status !== BookingStatus.Cancelled && (
        <div className="mt-3">
          <AddOneOffMemberPicker
            members={members}
            excludedMemberIds={[...roster.memberIds, ...entries.map((e) => e.memberId)]}
            onAdd={handleAdd}
            label={t("waitlist.add", "Add to waitlist")}
          />
        </div>
      )}
    </div>
  );
};

export default SessionWaitlistCard;
